import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { Caption, HeadingMBold, BodyMBold, BodyM } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import moment from 'moment';
import { getLanguage } from 'utils/language/language';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomFilter, { FilterValue } from 'components/CustomFilter/CustomFilter';
import TableHeader from 'components/TableHeader/TableHeader';
import Table from 'components/CustomTable/CustomTable';
import { columns, connectionList } from 'views/Numbers/Details/Usage/common/ShowPermission';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/usageStyle';
import { makeStyles } from '@material-ui/core';
import ConnectionTypeIcon from './ConnectionTypeIcon';
import { UsageRow } from 'utils/api/models/numbers/usageHistory';
import { getFirstDayOfMonth, getLastDayOfMonth } from 'utils/date/getDate';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { NumbersService } from 'services/numbers/numbers';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { useDispatch } from 'react-redux';
import numeral from 'numeral';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import CustomSelect from 'components/CustomSelect/CustomSelect';
import { exportToXLS } from 'utils/export/export';
import { TextCell, IconCell } from 'components/Cells/Cells';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPeriod {
	startDate: moment.Moment;
	endDate: moment.Moment;
}

interface ICallDetailsProps {
	accountType: string;
	billingType: string;
	phoneNumber: string;
	accountNumber: string;
}

const getSeconds = (value: string, units: types.usage.units): number => {
	const _value: number = Number(value);
	switch (units) {
		case 'HOURS':
			return _value * 60 * 60;
		case 'MINUTES':
			return _value * 60;
		case 'SECONDS':
		default:
			return _value;
	}
};

const formatTime = (value: string, units: types.usage.units) => {
	const seconds = getSeconds(value, units);
	const duration = moment.duration(seconds, 's');
	return [duration.get('s'), duration.get('m'), duration.get('h')].join(':').replace(/\b(\d)\b/g, '0$1');
};

const getCallDescription = (original: types.usage.IDataFormat) => {
	if (original['Type'] === 'DATA') {
		return 'DATA';
	} else if (original['Type'] === 'SMS') {
		return 'SMS';
	} else if (original['Type'] === 'VOICE' && original['Description'] === 'VOICE INTERNATIONAL') {
		return 'VOICEINTERNATIONAL';
	} else if (original['Type'] === 'CPN') {
		return 'CPN';
	} else {
		return 'VOICELOCAL';
	}
};

const CallDetails = ({ accountType, billingType, phoneNumber, accountNumber }: ICallDetailsProps) => {
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [data, setData] = useState<UsageRow[]>([]);
	const [filteredData, setFilteredData] = useState<UsageRow[]>([]);
	const [bill, setBill] = useState<string>('1');
	const [filters, setFilters] = useState<FilterValue>({
		period: [],
		costs: [],
		type: [],
	});
	const [period, setPeriod] = useState<IPeriod>({
		startDate: getFirstDayOfMonth(),
		endDate: getLastDayOfMonth(),
	});
	const [loading, setLoading] = useState<boolean>(false);
	const classes = useStyles();
	const dispatch = useDispatch();
	const fileName: string = `${phoneNumber} ${moment().format('LLL')}`;
	const emptyFilters: boolean = Object.values(filters).every((filter) => filter.length === 0);
	const isMounted = useRef(true);

	const billPeriodDates = useMemo(
		() => [
			moment()
				.subtract(bill, 'month')
				.startOf('month'),
			moment()
				.subtract(bill, 'month')
				.endOf('month'),
		],
		[bill]
	);

	const filterChanged = useCallback(
		(name, value) => {
			setFilters((filters) => ({ ...filters, [name]: value }));
		},
		[setFilters]
	);

	const columnsComponents = {
		date: {
			Header: 'Date',
			accessor: (row: types.usage.IDataFormat) => moment(row['Date'], 'DD.MM.YYYY').format('DD.MM.YYYY'),
			Cell: ({ original }: { original: types.usage.IDataFormat }) => <TextCell text={original['Date']} />,
			id: 'date-column',
		},
		hour: {
			Header: 'Hour',
			accessor: 'Hour',
			Cell: ({ original }: { original: types.usage.IDataFormat }) => <TextCell text={original['Hour']} />,
		},
		connectionType: {
			Header: 'Connection Type',
			Cell: ({ original }: { original: types.usage.IDataFormat }) => (
				<ConditionalRender
					show={!!original['Type']}
					onTrue={() => (
						<IconCell
							text={translate(`numbers.usage.connectionType.${getCallDescription(original)}`)}
							icon={<ConnectionTypeIcon type={original['Type']} />}
						/>
					)}
				/>
			),
			accessor: 'Type',
		},
		number: {
			Header: 'Number',
			id: 'number-column',
			accessor: 'Number',
			Cell: ({ original }: { original: types.usage.IDataFormat }) => <TextCell text={original['Number']} />,
		},
		duration: {
			Header: 'Time duration/Transfer',
			id: 'duration-column',
			accessor: 'Time duration/Transfer',
			Cell: ({ original }: { original: types.usage.IDataFormat }) => (
				<TextCell text={original['Time duration/Transfer']} />
			),
		},
		cost: {
			Header: 'Cost',
			accessor: (row: types.usage.IDataFormat) => row['Cost'],
			id: 'cost-column',
			Cell: ({ original }: { original: types.usage.IDataFormat }) => (
				<>
					<span className={classes.connectionTypeCellValue}>
						<BodyMBold inline disableMargin>
							{original['Cost']}
						</BodyMBold>
					</span>
					<BodyM inline disableMargin>
						{translate('OMR')}
					</BodyM>
				</>
			),
		},
	};

	const filterList = connectionList[accountType.toLowerCase()][billingType.toLowerCase()];

	const startDateValid = (date: moment.Moment): boolean => {
		return !period.endDate || date.isSameOrBefore(period.endDate, 'day');
	};

	const endDateValid = (date: moment.Moment): boolean => {
		return !period.startDate || date.isSameOrAfter(period.startDate, 'day');
	};

	const filterElements = {
		period: [
			{
				title: translate('numbers.usage.currentPeriod'),
				value: 'current',
			},
			{
				title: translate('numbers.usage.customPeriod'),
				value: 'custom',
				show: (
					<>
						<div className={classes.datePicker}>
							<CustomDatePicker
								timeFormat={false}
								value={period.startDate}
								onChange={(date) => {
									if (moment.isMoment(date)) {
										setPeriod((value) => ({
											...value,
											startDate: date,
										}));
									}
								}}
								isValidDate={startDateValid}
							/>
						</div>
						<div className={classes.datePicker}>
							<CustomDatePicker
								timeFormat={false}
								value={period.endDate}
								onChange={(date) => {
									if (moment.isMoment(date)) {
										setPeriod((value) => ({
											...value,
											endDate: date,
										}));
									}
								}}
								isValidDate={endDateValid}
							/>
						</div>
					</>
				),
				tagText: `${period.startDate.format('DD/MM/YYYY')} - ${period.endDate.format('DD/MM/YYYY')}`,
			},
			{
				title: translate('numbers.usage.billPeriod'),
				value: 'billPeriod',
				show: (
					<>
						<CustomSelect
							formControlProps={{
								fullWidth: true,
							}}
							items={Array(12)
								.fill(0)
								.map((_, ind) => ind + 1)
								.map((month) => ({
									label: moment()
										.subtract(month, 'month')
										.startOf('month')
										.format('DD.MM.YYYY'),
									value: month,
								}))}
							value={bill}
							onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
								setBill(event.target.value);
							}}
						/>
					</>
				),
				tagText: `${billPeriodDates[0].format('DD/MM/YYYY')} - ${billPeriodDates[1].format('DD/MM/YYYY')}`,
			},
		],
		costs: [
			{
				title: translate('numbers.usage.included'),
				value: 'included',
			},
			{
				title: translate('numbers.usage.extra'),
				value: 'extra',
			},
		],
		type: [
			{
				title: translate('numbers.usage.onlyData'),
				value: 'DATA',
			},
			{
				title: translate('numbers.usage.onlyVoice'),
				value: 'VOICE',
			},
			{
				title: translate('numbers.usage.onlySMS'),
				value: 'SMS',
			},
		],
	};

	useEffect(() => {
		const [startPeriod, endPeriod] = (() => {
			const periodValue = filters.period.length > 0 ? filters.period[0] : '';
			switch (periodValue) {
				case 'current':
					return [getFirstDayOfMonth(), getLastDayOfMonth()];

				case 'billPeriod':
					return billPeriodDates;

				case 'custom':
					return [period.startDate, period.endDate];
				default:
					return [moment().subtract(30, 'days'), moment()];
			}
		})();
		setLoading(true);
		dispatch(showLoadingIndicator());
		NumbersService.getUsageHistory({
			startPeriod: startPeriod.format('YYYY-MM-DDT00:00:00'),
			endPeriod: endPeriod.format('YYYY-MM-DDT23:59:59'),
			phoneNumber,
			accountNumber,
			accountType,
			billingType,
			type: filters.type.length === 0 ? ['DATA', 'VOICE', 'SMS'] : filters.type,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					if (isMounted.current) {
						setLoading(false);
					}
				})
			)
			.subscribe(
				({ usage }) => {
					if (isMounted.current && !!usage) {
						setData(usage);
					}
				},
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [
		filters.period,
		filters.type,
		phoneNumber,
		accountNumber,
		accountType,
		billingType,
		billPeriodDates,
		period,
		dispatch,
	]);

	useEffect(() => {
		const lowerCaseSearchInputValue: string = searchInputValue.toLowerCase();
		setFilteredData(
			data.filter((row: UsageRow) => {
				const number = row['destinationNumber'].toLowerCase().indexOf(lowerCaseSearchInputValue) >= 0;
				const type = row['type'].toLowerCase().indexOf(lowerCaseSearchInputValue) >= 0;
				const filter =
					filters.costs.length === 0 ||
					(filters.costs.indexOf('included') >= 0 && row.cost === '0') ||
					(filters.costs.indexOf('extra') >= 0 && row.cost !== '0');
				return (number || type) && filter;
			})
		);
	}, [data, searchInputValue, filters.costs]);

	const formattedData: types.usage.IDataFormat[] = useMemo(() => {
		return filteredData.map((row) => {
			return {
				Date: moment(row.startDateTime).format('DD.MM.YYYY'),
				Hour: moment(row.startDateTime).format('hh:mm A'),
				'Connection Type': row.type,
				Type: row.type,
				Description: row.description,
				Number: row.type === 'DATA' ? translate('numberDetails.usage.internetType') : row.destinationNumber,
				'Time duration/Transfer': (row.duration || null) && formatTime(row.duration, row.units),
				Cost: row.cost === '0' ? '0' : numeral(row.cost).format('0.000'),
			};
		});
	}, [filteredData]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<>
			<GridContainer className={classes.connectionListContainer}>
				<GridItem xs={12}>
					<div className={classes.connectionList}>
						<HeadingMBold inline color="secondary-light" disableMargin>
							{translate('numbers.usage.connectionList')}
						</HeadingMBold>
					</div>
					<Caption inline color="secondary-light">
						{translate('numbers.usage.lastDays', '30')}
					</Caption>
				</GridItem>
				<GridItem xs={12}>
					<Caption color="secondary-light" disableMargin>
						{translate(
							'numbers.usage.lastUpdate',
							moment()
								.locale(getLanguage())
								.fromNow()
						)}
					</Caption>
					<Caption color="secondary-light" disableMargin>
						{translate('numbers.usage.dataDelays')}
					</Caption>
				</GridItem>
			</GridContainer>

			<ConditionalRender
				show={data?.length === 0 && emptyFilters}
				onTrue={() => (
					<ConditionalRender
						show={!loading}
						onTrue={() => (
							<div className={classes.center}>
								<p className={classes.noCalls}>{translate('numbers.usage.noCalls')}</p>
							</div>
						)}
					/>
				)}
				onFalse={() => (
					<div>
						<CustomFilter
							value={filters}
							onChange={filterChanged}
							filters={filterList.map((name: string) => ({
								title: translate(`numbers.usage.${name}Accordion`),
								name,
								type: name === 'period' ? 'one' : 'multiple',
								elements: filterElements[name],
								disableOnBlur: name === 'period',
							}))}
						/>
						<TableHeader
							setSearchInputValue={(value) => setSearchInputValue(value)}
							searchInputValue={searchInputValue}
							exportToXLS={() => exportToXLS(formattedData, fileName)}
						/>
						<Table
							total={formattedData.length}
							withHeader
							tableProps={{
								data: formattedData,
								loading,
								columns: columns[accountType.toLowerCase()][billingType.toLowerCase()]
									.map((value: string) => columnsComponents[value])
									.filter(Boolean),
							}}
						/>
					</div>
				)}
			/>
		</>
	);
};

export default CallDetails;
